import { Component } from "react";
import TopNav from "../components/top_nav";
import Footer from "../components/footer";
import { ArrowRight } from "react-bootstrap-icons";
import { useNavigate } from "react-router-dom";



const features = [
    {
        title: "Multi-Protocol Support",
        description:
            "Converts data from various proprietary sensor protocols (e.g., Modbus, CAN, OPC, BACnet) into a unified, standard protocol for easy integration.",
    },
    {
        title: "Real-Time Data Conversion",
        description:
            "Provides real-time data translation, ensuring that downstream systems receive up-to-date information without delays or errors.",
    },
    {
        title: "Seamless Integration",
        description:
            "Allows for the integration of sensors from different OEMs into a single, unified system, reducing the need for multiple communication interfaces.",
    },
    {
        title: "Scalable and Modular Design",
        description:
            "Designed to scale with your system’s growing needs, allowing for easy addition of new protocols and devices as they emerge.",
    },
    {
        title: "Data Validation and Error Detection",
        description:
            "Automatically checks for inconsistencies or errors in data to ensure smooth communication between systems.",
    },
    {
        title: "Secure Communication",
        description:
            "Features built-in security protocols to ensure that data transfers are protected from unauthorized access or interference.",
    },
];


const metrics = [
    {
        title: "Data Conversion Speed",
        description:
            "99% accuracy in real-time data conversion with an average latency of less than 200 milliseconds.",
    },
    {
        title: "Protocol Compatibility",
        description:
            "Supports over 50+ different sensor protocols, making it adaptable to various industry needs.",
    },
    {
        title: "Data Transfer Volume",
        description:
            "Scalable to high volumes of data per month across multiple endpoints.",
    },
    {
        title: "Device Integration",
        description:
            "Successfully integrated devices from multiple OEMs across various industries without issues.",
    },
    {
        title: "System Uptime",
        description:
            "Achieved 99.99% uptime during integration and operational phases.",
    },
];



const impacts = [
    {
        icon: '🚆',
        title: '30% Increase in Yard Throughput',
        description: 'Daily asset movement rose from 80 to 105, improving operational flow.',
        color: 'text-primary',
    },
    {
        icon: '🚫',
        title: '100% Collision Elimination',
        description: 'Zero head-on or rear-end incidents reported after deployment.',
        color: 'text-danger',
    },
    {
        icon: '📍',
        title: '2 cm Tracking Accuracy',
        description: 'Achieved with RTK GPS for precise loco and torpedo positioning.',
        color: 'text-info',
    },
    {
        icon: '⚡',
        title: '95% Faster Obstacle Response',
        description: 'Instant alerts and automated braking reduced reaction time from ~5 minutes to seconds.',
        color: 'text-warning',
    },
];




const ProductsScreen = () => {
    const navigate = useNavigate();

    return (
        <div>
            <TopNav />

            <div className="row justify-content-center mx-0">
                <div className="col-md-10 px-0">

                    <div className="my-4 pt-4">
                        <button
                            onClick={() => navigate(-1)}
                            className="btn btn-link px-0 d-flex align-items-center gap-2 fs-18 text-black text-decoration-none">
                            <i className="bi bi-chevron-left text-primary-blue fw-bold"></i>Back to Solutions
                        </button>
                    </div>

                    <div className="text-center my-5">
                        <label className="fs-48 fw-bold">Protocol Converter</label>
                    </div>


                    <div className="row align-items-center mx-0 mt-5">
                        <div className="col-lg-6 mb-4 mb-lg-0 pe-5">
                            <h2 className="text-primary-blue fw-bold fs-36">Project Overview</h2>
                            <p className="fs-18 mt-2">
                                The <strong>Protocol Converter</strong> is an advanced solution designed to bridge
                                communication gaps between multiple sensor-based systems and downstream systems. By converting
                                diverse protocols into a standardized format, the protocol converter enables seamless data
                                exchange between different OEMs, ensuring compatibility and interoperability.
                            </p>

                            <div className="d-flex gap-5 mt-5 pt-3">
                                <div className="text-center">
                                    <h3 className="fw-bold fs-30">500 +</h3>
                                    <p className="text-muted mb-0 fs-16">Assets Monitored</p>
                                </div>
                                <div className="text-center">
                                    <h3 className="fw-bold fs-30">50 +</h3>
                                    <p className="text-muted mb-0 fs-16">Point Machines Automated</p>
                                </div>
                            </div>
                        </div>

                        {/* Image Section */}
                        <div className="col-lg-6">
                            <img
                                src="/solution6.webp"
                                alt="Project Visual"
                                className="img-fluid rounded shadow-sm"
                            />
                        </div>
                    </div>



                    <div className="mt-5 pt-5 px-3">
                        <h3 className="text-primary-blue fw-bold fs-36">Features</h3>
                        <p className="fs-18 mt-3">
                            This solution simplifies system integration, reduces complexity, and enhances operational
                            efficiency for industries utilizing a variety of sensor-based technologies.
                        </p>

                        <div className="row mt-4">
                            {/* Feature Cards */}
                            <div className="col-lg-9 mb-4 mb-lg-0">
                                <div className="row g-3">
                                    {features.map((feature, index) => (
                                        <div className="col-md-4 d-flex" key={index}>
                                            <div className="feature-card p-3 w-100 d-flex flex-column justify-content-between">
                                                <h6 className="fw-bold mb-2 fs-22">{feature.title}</h6>
                                                <p className="mb-0 text-muted fs-16">{feature.description}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Image */}
                            <div className="col-lg-3">
                                <img
                                    src="/solution6.webp"
                                    alt="Feature Visual"
                                    className="img-fluid rounded shadow-sm w-100"
                                    style={{ objectFit: 'cover', height: '100%' }}
                                />
                            </div>
                        </div>
                    </div>


                    <div className="mt-5 px-3 pt-5">
                        <h4 className="text-primary-blue fw-bold fs-36">Metrics</h4>
                        <p className="fs-18 mt-3">
                            These performance metrics highlight the measurable impact of the Yard Safety Management Solution,
                            demonstrating significant gains in safety, operational efficiency, and real-time asset visibility
                            across complex yard environments.
                        </p>

                        <div className="row row-cols-1 row-cols-md-3 row-cols-lg-5 g-4 my-3">
                            {metrics.map((metric, index) => (
                                <div className="" key={index}>
                                    <div className="metric-card h-100 bg-white shadow-sm rounded">
                                        <h6 className="fw-bold mb-2 fs-20">{metric.title}</h6>
                                        <p className="text-muted mb-0 fs-16">{metric.description}</p>
                                    </div>
                                </div>
                            ))}
                        </div>

                    </div>



                    <div className="mt-5 pt-5 px-3">
                        <h4 className="text-primary-blue fw-bold fs-36">Outcome</h4>
                        <p className="fs-18 mt-3">
                            The <strong>Protocol Converter</strong> has significantly streamlined communication between heterogeneous systems,
                            reducing the time spent on manual data mapping and enhancing the overall efficiency of the operation. By standardizing
                            communication protocols, the solution has enabled seamless integration of various sensor devices, thereby improved
                            operational reliability and minimized system downtime. Additionally, the solution’s scalability allows businesses
                            to expand their systems without worrying about protocol mismatches or integration challenges.
                        </p>
                    </div>



                    <div className="my-5 px-3">
                        <div className="row align-items-stretch g-4">
                            {/* Left column with image */}
                            <div className="col-lg-6 pe-0">
                                <div style={{ height: '610px', overflow: 'hidden' }}>
                                    <img
                                        src="/solution6.webp"
                                        alt="Train yard"
                                        style={{ objectFit: 'cover', height: '100%', width: '100%' }}
                                        className="img-fluid shadow-sm"
                                    />
                                </div>
                            </div>

                            <div className="col-lg-6 d-flex flex-column justify-content-between" style={{ height: '610px' }}>
                                <div className="row row-cols-1 row-cols-md-2 gx-2 gy-2 h-100">
                                    {impacts.map((impact, index) => (
                                        <div className="col" key={index}>
                                            <div className="d-flex flex-column justify-content-center align-items-center h-100 border-black rounded-4 p-4 shadow-sm hover-lift">
                                                <h6 className={`fw-bold mb-1 fs-20 w-100`}>
                                                    {impact.icon} {impact.title}
                                                </h6>
                                                <p className="text-muted mb-0 fs-16">{impact.description}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>











            <Footer />
        </div>
    );
}




export default ProductsScreen;